<template>
    <div id="customLoginForm">
        <div>
            <b-row v-if="!customInputsIsValid">
                <b-col lg="12">
                    <a-alert :key="`alertContainer-${$i18n.locale}`" class="mb-2" showIcon type="warning">
                        <div slot="description">
                            {{ $t("message.customLoginAlert") }}
                        </div>
                    </a-alert>
                </b-col>
            </b-row>
            <b-row>
                <b-col lg="4">
                    <draggable :sort="false">
                        <md-button
                            v-for="(field, i) in formInputs"
                            :key="i"
                            v-tippy="{
                                followCursor: true,
                                placement: 'bottom-end',
                                theme: isDarkMode ? 'light-border' : 'google',
                            }"
                            :content="getFieldDescription(field.type)"
                            :disabled="isDisable(field.type)"
                            class="fields p-0 m-0"
                            @click="addNewInput(field)"
                        >
                            <md-icon>add</md-icon>
                            <span>{{
                                field.type === "subject" ? $t("message.department") : $t("message." + field.type)
                            }}</span>
                        </md-button>
                    </draggable>
                </b-col>
                <b-col lg="8">
                    <draggable id="list" :list="customInputs" group="types" @change="dragChange">
                        <div v-for="(field, i) in customInputs" :key="i" class="fields">
                            <div
                                v-tippy="{
                                    followCursor: true,
                                    placement: 'bottom-end',
                                    theme: isDarkMode ? 'light-border' : 'google',
                                }"
                                :content="$t('message.doubleClickEdit')"
                                class="infoF"
                                v-on:dblclick="
                                    !['email', 'fullname'].includes(field.type) ? handleEdit(field) : () => {}
                                "
                            >
                                <md-icon>drag_handle</md-icon>
                                <span>{{ field.localizedLabel[$i18n.locale].text.toUpperCase() }}</span
                                >:
                                <span>{{
                                    field.type === "subject" ? $t("message.department") : $t("message." + field.type)
                                }}</span>
                            </div>
                            <div class="actions">
                                <md-button
                                    v-tippy="{
                                        followCursor: true,
                                        placement: 'bottom-end',
                                        theme: isDarkMode ? 'light-border' : 'google',
                                    }"
                                    :content="
                                        field.visible ? $t('message.visibleField') : $t('message.notVisibleField')
                                    "
                                    @click="toggleProperty(field, 'visible')"
                                >
                                    <md-icon
                                        :style="{
                                            color: field.visible ? 'green' : 'gray',
                                        }"
                                    >
                                        {{ field.visible ? "visibility" : "visibility_off" }}
                                    </md-icon>
                                </md-button>
                                <md-button
                                    v-tippy="{
                                        followCursor: true,
                                        placement: 'bottom-end',
                                        theme: isDarkMode ? 'light-border' : 'google',
                                    }"
                                    :content="
                                        field.required ? $t('message.requiredField') : $t('message.notRequiredField')
                                    "
                                    @click="toggleProperty(field, 'required')"
                                >
                                    <md-icon
                                        :style="{
                                            color: field.required ? 'goldenrod' : 'gray',
                                        }"
                                        >priority_high
                                    </md-icon>
                                </md-button>
                                <md-button
                                    v-if="!['email', 'fullname'].includes(field.type)"
                                    v-tippy="{
                                        followCursor: true,
                                        placement: 'bottom-end',
                                        theme: isDarkMode ? 'light-border' : 'google',
                                    }"
                                    :content="$t('message.edit')"
                                    class="md-primary"
                                    @click="handleEdit(field)"
                                >
                                    <md-icon>edit</md-icon>
                                </md-button>
                                <md-button
                                    v-tippy="{
                                        followCursor: true,
                                        placement: 'bottom-end',
                                        theme: isDarkMode ? 'light-border' : 'google',
                                    }"
                                    :content="$t('message.delete')"
                                    class="md-accent"
                                    @click="handleDelete(field._id)"
                                >
                                    <md-icon>delete</md-icon>
                                </md-button>
                            </div>
                        </div>
                        <div slot="footer">
                            <button class="addButton" @click="addNewInput">
                                <strong>+</strong>
                                {{ $t("message.addNewField") }}
                            </button>
                        </div>
                    </draggable>
                </b-col>
            </b-row>
        </div>

        <b-modal
            id="newFieldModal"
            :title="$t(editMode ? 'message.editField' : 'message.addNewField')"
            hide-footer
            size="lg"
            @hide="submitClicked = false"
        >
            <a-alert
                v-if="($validator.errors.items.length || !isOneLanguageIncludes()) && submitClicked"
                :message="$t('message.error')"
                class="mb-2"
                showIcon
                type="error"
            >
                <div slot="description">
                    <div v-for="err of $validator.errors.items">
                        <span>
                            {{ err.msg }}
                        </span>
                    </div>
                    <div v-if="!isOneLanguageIncludes()">
                        <span>
                            {{ $t("message.atLeastOneLanguage") }}
                        </span>
                    </div>
                </div>
            </a-alert>
            <b-form @submit.prevent="handleSubmit">
                <b-row class="mb-1">
                    <b-col>
                        <label>{{ $t("message.name") }}</label>
                        <b-form-group>
                            <b-form-input
                                v-model="input.name"
                                v-validate="'required'"
                                :name="$t('message.name')"
                                trim
                            ></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <label>{{ $t("message.type") }}</label>
                        <b-form-group>
                            <b-form-select v-model="input.type" name="type">
                                <option value="text">
                                    {{ $t("message.text") }}
                                </option>
                                <option value="numeric">
                                    {{ $t("message.numeric") }}
                                </option>
                                <option value="phone">
                                    {{ $t("message.phone") }}
                                </option>
                                <option value="select">
                                    {{ $t("message.select") }}
                                </option>
                                <option v-if="!isDisable('city')" value="city">
                                    {{ $t("message.city") }}
                                </option>
                                <option v-if="!isDisable('cityAndDistrict')" value="cityAndDistrict">
                                    {{ $t("message.cityDistrict") }}
                                </option>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="mb-1">
                    <b-col>
                        <span id="visibilityid">{{ $t("message.visible") }}</span>
                        <md-switch v-model="input.visible" name="visible"></md-switch>
                    </b-col>
                    <b-col>
                        <span id="requiredid">{{ $t("message.required") }}</span>
                        <md-switch v-model="input.required" name="required"></md-switch>
                    </b-col>
                </b-row>
                <b-tabs class="mb-1">
                    <b-tab v-for="(lang, i) in languages" :key="i" :title="lang.name">
                        <label class="pt-1">{{ $t("message.label") }}</label>
                        <b-form-group class="mt-1">
                            <b-form-input
                                v-model="input.localizedLabel[lang.code].text"
                                :name="`label[${lang.code}]`"
                                :placeholder="$t('message.clPlaceholder')"
                                trim
                            ></b-form-input>
                        </b-form-group>
                    </b-tab>
                </b-tabs>
                <div v-if="input.type === 'select'" style="max-height: 300px; overflow-y: scroll; overflow-x: hidden">
                    <b-row class="mt-1">
                        <b-col>
                            <label>{{ $t("message.options") }}</label>
                            <b-row v-for="(option, i) in input.options" :key="i">
                                <b-col class="d-flex align-items-center">
                                    <b-form-group>
                                        <b-form-radio
                                            v-model="input.defaultOption"
                                            :value="input.options[i]"
                                            name="defaultOption"
                                        />
                                    </b-form-group>

                                    <b-form-group class="w-100">
                                        <b-form-input
                                            v-model="input.options[i]"
                                            v-validate="'required'"
                                            :name="'options-' + i"
                                            :placeholder="$t('message.optionsPlaceholder')"
                                            trim
                                        />
                                    </b-form-group>

                                    <md-button
                                        class="mb-4"
                                        style="width: 42px; min-width: 42px"
                                        @click="input.options.splice(i, 1)"
                                    >
                                        <md-icon>delete</md-icon>
                                    </md-button>
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <md-button @click="input.options.push('')">
                                <md-icon>add</md-icon>
                            </md-button>
                        </b-col>
                    </b-row>
                </div>
                <b-row class="mt-3">
                    <b-col>
                        <b-button @click="() => this.$bvModal.hide('newFieldModal')">
                            {{ $t("message.cancel") }}
                        </b-button>
                    </b-col>
                    <b-col>
                        <b-button type="submit" variant="primary">
                            {{ editMode ? $t("message.update") : $t("message.save") }}
                        </b-button>
                    </b-col>
                </b-row>
            </b-form>
        </b-modal>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import { mapActions, mapGetters, mapState } from "vuex";
import _ from "lodash";
import createLogger from "../utils/log2";

const log = createLogger("CustomLoginForm");

const createInitialInput = (type) => ({
    name: "",
    required: false,
    visible: true,
    order: 0,
    type,
    localizedLabel: {
        tr: {
            text: "",
            languageCode: "tr",
        },
        en: {
            text: "",
            languageCode: "en",
        },
        ru: {
            text: "",
            languageCode: "en",
        },
    },
});
export default {
    name: "CustomLoginForm",
    components: {
        draggable,
    },
    data() {
        return {
            formInputs: [
                {
                    name: "Fullname",
                    required: true,
                    visible: true,
                    order: 0,
                    type: "fullname",
                    localizedLabel: {
                        tr: {
                            languageCode: "tr",
                            text: "Ad Soyad",
                        },
                        en: {
                            languageCode: "en",
                            text: "Fullname",
                        },
                        ru: {
                            languageCode: "ru",
                            text: "ФИО",
                        },
                    },
                },
                {
                    name: "Email",
                    required: true,
                    visible: true,
                    order: 0,
                    type: "email",
                    localizedLabel: {
                        tr: {
                            languageCode: "tr",
                            text: "E-Posta",
                        },
                        en: {
                            languageCode: "en",
                            text: "Email",
                        },
                        ru: {
                            languageCode: "ru",
                            text: "е-мейл",
                        },
                    },
                },
                {
                    name: "",
                    required: false,
                    visible: true,
                    order: 0,
                    type: "text",
                    localizedLabel: {},
                },
                {
                    name: "",
                    required: false,
                    visible: true,
                    order: 0,
                    type: "numeric",
                    localizedLabel: {},
                },
                {
                    name: "",
                    required: false,
                    visible: true,
                    order: 0,
                    type: "phone",
                    localizedLabel: {},
                },
                {
                    name: "",
                    required: false,
                    visible: true,
                    order: 0,
                    type: "select",
                    options: [""],
                    defaultOption: "",
                    localizedLabel: {},
                },
                {
                    name: "subject",
                    required: true,
                    visible: true,
                    order: 0,
                    type: "subject",
                    localizedLabel: {
                        tr: {
                            languageCode: "tr",
                            text: "Departman",
                        },
                        en: {
                            languageCode: "en",
                            text: "Department",
                        },
                        ru: {
                            languageCode: "ru",
                            text: "тема",
                        },
                    },
                },
                {
                    name: "City",
                    required: true,
                    visible: true,
                    order: 0,
                    type: "city",
                    localizedLabel: {
                        tr: {
                            languageCode: "tr",
                            text: "Şehir",
                        },
                        en: {
                            languageCode: "en",
                            text: "City",
                        },
                        ru: {
                            languageCode: "ru",
                            text: "город",
                        },
                    },
                },
                {
                    name: "City&District",
                    required: false,
                    visible: true,
                    order: 0,
                    type: "cityAndDistrict",
                    localizedLabel: {
                        tr: {
                            languageCode: "tr",
                            text: "Şehir/İlçe",
                        },
                        en: {
                            languageCode: "en",
                            text: "City/District",
                        },
                        ru: {
                            languageCode: "ru",
                            text: "город/округ",
                        },
                    },
                },
            ],
            input: _.cloneDeep(createInitialInput("text")),
            editMode: false,
            submitClicked: false,
            disableButton: false,
        };
    },
    computed: {
        ...mapState({
            languages: (state) => _.sortBy(state.language.languages, "order"),
            customInputs: (state) => state.customLoginForm.inputs,
            customInputsIsValid: (state) => !!state.customLoginForm.inputs.filter((x) => x.visible).length,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
    },
    methods: {
        ...mapActions({
            createInput: "customLoginForm/createInput",
            getCustomLoginFormInputs: "customLoginForm/getCustomLoginFormInputs",
            updateInput: "customLoginForm/updateInput",
            multiUpdateInput: "customLoginForm/multiUpdateInput",
            deleteInput: "customLoginForm/deleteInput",
        }),
        isOneLanguageIncludes() {
            let field = this.input.localizedLabel;
            return Object.keys(field).some((lang) => field[lang].text !== "");
        },
        dragChange(events) {
            let fixedInputs = this.customInputs.map((x, i) => ({
                ...x,
                order: i,
            }));
            log("Drag change fixedInputs", { fixedInputs });
            this.multiUpdateInput(fixedInputs);
        },
        addNewInput({ type }) {
            this.editMode = false;
            const input = _.cloneDeep(createInitialInput(type || "text"));
            input["order"] = this.customInputs.length;
            if (["email", "fullname", "city", "cityAndDistrict", "subject"].includes(type)) {
                let maybeInput = this.formInputs.find((x) => x.type === type);
                if (maybeInput) {
                    input.name = maybeInput.name;
                    input.localizedLabel = maybeInput.localizedLabel;
                    input.required = maybeInput.required;
                    input.visible = maybeInput.visible;
                }
                log("inputu logladık", { input });
                this.disableButton = true;
                this.createInput(input).then((response) => {
                    this.disableButton = false;
                });
            } else {
                if (type === "select") {
                    let maybeInput = this.formInputs.find((x) => x.type === "select");
                    if (maybeInput) {
                        maybeInput = _.cloneDeep(maybeInput);
                        input.options = maybeInput.options;
                        input.defaultOption = maybeInput.defaultOption;
                    }
                }
                log("inputu logladık", { input });
                this.input = input;
                this.$bvModal.show("newFieldModal");
            }
        },
        handleSubmit() {
            this.submitClicked = true;
            this.$validator.validateAll().then((isSuccess) => {
                if (isSuccess && this.isOneLanguageIncludes()) {
                    let input = _.cloneDeep(this.input);
                    input.localizedLabel = this.editLocalizedFields(input.localizedLabel);
                    let p;

                    if (!this.editMode) {
                        input["order"] = this.customInputs.length;
                        p = this.createInput(input);
                    } else {
                        p = this.updateInput(input);
                        this.editMode = false;
                    }
                    p.then((x) => {
                        this.$toasted.show(this.$t("message.userSaved"), {
                            duration: 3000,
                            position: "bottom-center",
                        });
                    }).catch((err) => {
                        const errMsg =
                            err.response && err.response.data && err.response.data.localizedMessages
                                ? err.response.data.localizedMessages[this.$i18n.locale]
                                : err.message;
                        this.$toasted.error(errMsg, {
                            duration: 4000,
                            position: "bottom-center",
                        });
                    });
                    this.input = _.cloneDeep(createInitialInput("text"));
                    this.$bvModal.hide("newFieldModal");
                }
            });
        },
        editLocalizedFields(field) {
            let find = Object.keys(field).find((lang) => field[lang].text !== "");
            if (find) {
                let empties = Object.keys(field).filter((lang) => field[lang].text === "");
                empties.forEach((item) => (field[item].text = field[find].text));
                log.debug({
                    field,
                    find,
                    empties,
                });
            }
            return field;
        },
        async handleDelete(id) {
            if (id) {
                await this.deleteInput(id);
                let fixedInputs = _.cloneDeep(this.customInputs);
                fixedInputs = fixedInputs.map((x, i) => ({
                    ...x,
                    order: i,
                }));
                await this.multiUpdateInput(fixedInputs);
            }
        },
        handleEdit(field) {
            this.editMode = true;
            this.input = _.cloneDeep(field);
            this.$bvModal.show("newFieldModal");
        },
        moveUp(field) {
            let inputs = this.customInputs;
            const findIndex = inputs.findIndex((x) => x._id === field._id);
            const temp = inputs[findIndex];
            inputs[findIndex] = inputs[findIndex - 1];
            inputs[findIndex - 1] = temp;
            let fixedInputs = inputs.map((x, i) => ({ ...x, order: i }));
            this.multiUpdateInput(fixedInputs);
        },
        toggleProperty(field, property) {
            this.editMode = true;
            let input = _.cloneDeep(field);
            input[property] = !input[property];
            switch (property) {
                case "required":
                    if (input.required) input.visible = true;
                    break;
                case "visible":
                    if (!input.visible) input.required = false;
                    break;
            }
            this.updateInput(input);
        },
        moveDown(field) {
            let inputs = this.customInputs;
            const findIndex = inputs.findIndex((x) => x._id === field._id);
            const temp = inputs[findIndex];
            inputs[findIndex] = inputs[findIndex + 1];
            inputs[findIndex + 1] = temp;
            let fixedInputs = inputs.map((x, i) => ({ ...x, order: i }));
            this.multiUpdateInput(fixedInputs);
        },
        isDisable(type) {
            return (
                (this.customInputs.map((x) => x.type).includes(type) &&
                    ["fullname", "email", "city", "cityAndDistrict", "subject"].includes(type)) ||
                (type === "cityAndDistrict" && this.customInputs.map((x) => x.type).includes("city")) ||
                (type === "city" && this.customInputs.map((x) => x.type).includes("cityAndDistrict")) ||
                (this.customInputs.map((x) => x.type).includes(type) && ["cityAndDistrict", "city"].includes(type)) ||
                this.disableButton
            );
        },
        getFieldDescription(type) {
            let result = "";
            if (type === "text") result = this.$t("message.textField");
            else if (type === "numeric") result = this.$t("message.numericField");
            else if (type === "phone") result = this.$t("message.phoneField");
            else if (type === "email") result = this.$t("message.emailField");
            else if (type === "subject") result = this.$t("message.departmentField");
            else if (type === "select") result = this.$t("message.selectField");
            else if (type === "fullname") result = this.$t("message.fullnameField");
            else if (type === "city") result = this.$t("message.city");
            else if (type === "cityAndDistrict") result = this.$t("message.cityDistrict");

            return result;
        },
    },
    mounted() {
        this.getCustomLoginFormInputs();
    },
};
</script>

<style lang="scss" scoped>
#customLoginForm {
    margin-bottom: 10%;
    border: 1px solid gray;
    padding: 1rem;

    #list {
        height: 100%;
    }

    .fields,
    .addButton {
        background-color: #ffffff;
        border-radius: 8px;
        margin-bottom: 10px !important;
        cursor: pointer;
        padding: 6px 14px;
        user-select: none;
        box-shadow: 0 0 3px #00000080;
    }

    .fields {
        display: flex;
        align-items: center;
        min-width: 100%;

        .infoF {
            flex: 1;
        }

        .actions {
            display: flex;
            min-height: 42px;

            button {
                min-width: 32px;
                width: 32px;
                height: 32px;
                border-radius: 50%;
            }
        }
    }

    .fields:hover {
        cursor: move;
    }

    .addButton {
        border-style: dashed;
        width: 100%;
        text-align: left;
    }
}
</style>

<style lang="scss">
.md-button.fields .md-ripple {
    justify-content: start;
}
</style>
